import React from 'react'
// libs
import { useSelector } from 'react-redux'
// context
import { RiskRegisterContext } from 'containers/Risks/RisksRegister/layers'
// store
import { riskTemplatesSelector } from 'store/reducers/riskTemplates'
// types
import { CustomColumnSource, IField } from 'types/graphql'

// One entry per custom column — sorting and filtering both treat the column as a whole and hand the
// backend every source it reads from.
export type CustomColumnField = IField & {
  fromCustomColumn: true
  customColumnSources: CustomColumnSource[]
}

/**
 * A custom column has no field of its own — it reads its value from fields that live on the forms
 * above the row. This resolves the columns of the current risk view into field-shaped descriptors
 * (type, options, response type) so they can be sorted and filtered like regular fields.
 */
export const useCustomColumnFields = () => {
  const { riskView } = React.useContext(RiskRegisterContext)

  const riskTemplates = useSelector(riskTemplatesSelector)

  return React.useMemo(() => {
    const columns = (riskView?.customColumns ?? []).map((column) => ({
      column,
      // Source order is the order the column reads its value in, so it is kept as is.
      sources: (column.sources ?? []).map(({ fieldId, templateId }) => {
        const template = riskTemplates.find((item) => item.id === templateId)

        return {
          fieldId,
          templateId,
          template,
          field: template?.fields.find((item) => item.id === fieldId)
        }
      })
    }))

    const columnFields: CustomColumnField[] = columns.flatMap(({ column, sources }) => {
      // The first source that resolves defines the shape of the column (type, options).
      const resolved = sources.find(({ field }) => field)

      if (!resolved) {
        return []
      }

      return [
        {
          ...resolved.field,
          id: column.id,
          label: column.name,
          multiple: false,
          fromCustomColumn: true as const,
          customColumnSources: sources.map(({ fieldId, templateId }) => ({ fieldId, templateId }))
        }
      ]
    })

    return { columnFields }
  }, [riskView, riskTemplates])
}
